// API 配置文件
export const API_BASE_URL = 'https://web.nftservice.io/api/';

/**
 * 获取完整的 API URL
 * @param endpoint - API 端点路径
 * @returns 完整的 API URL
 */
export const getApiUrl = (endpoint: string): string => {
  return `${API_BASE_URL}${endpoint}`;
};

/**
 * 处理登录过期
 */
const handleTokenExpired = () => {
  console.log('🔴 Token 已过期，清除缓存并跳转到首页');
  
  // 清除缓存的 token
  localStorage.removeItem('authToken');
  localStorage.removeItem('authTokenExpiresAt');
  
  // 触发登录状态变化事件
  window.dispatchEvent(new Event('authStatusChanged'));
  
  // 跳转到首页
  if (window.REACT_APP_NAVIGATE) {
    window.REACT_APP_NAVIGATE('/');
  } else {
    window.location.href = '/';
  }
};

/**
 * 通用 API 请求函数
 * @param endpoint - API 端点路径
 * @param options - fetch 请求配置
 * @returns Promise
 */
export const apiRequest = async <T = any>(
  endpoint: string,
  options: {
    method?: string;
    headers?: Record<string, string>;
    body?: any;
    [key: string]: any;
  } = {}
): Promise<T> => {
  const url = getApiUrl(endpoint);

  // 获取当前语言，默认 zh
  const currentLang = localStorage.getItem('language') || 'zh';
  const languageHeader = currentLang === 'en' ? 'en' : 'zh';

  const defaultHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    'Language': languageHeader,
  };

  const config: {
    method: string;
    headers: Record<string, string>;
    body?: string | FormData;
    mode: 'cors';
    credentials: 'omit';
  } = {
    method: options.method || 'POST',
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
    mode: 'cors',
    credentials: 'omit',
  };

  // 如果有 body 且不是 FormData，转换为 JSON 字符串
  if (options.body && !(options.body instanceof FormData)) {
    config.body = JSON.stringify(options.body);
  } else if (options.body instanceof FormData) {
    config.body = options.body;
    // FormData 会自动设置正确的 Content-Type，需要删除手动设置的
    delete config.headers['Content-Type'];
  }

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API 请求失败:', response.status, errorText);
      throw new Error(`请求失败: ${response.status}`);
    }

    // 先获取响应文本
    const responseText = await response.text();
    console.log('📥 API 响应文本:', responseText);
    
    // 检查响应是否为空
    if (!responseText || responseText.trim() === '') {
      console.error('❌ API 返回空响应');
      throw new Error('服务器返回空响应');
    }
    
    // 尝试解析 JSON
    let data;
    try {
      data = JSON.parse(responseText);
    } catch (parseError) {
      console.error('❌ JSON 解析失败:', parseError);
      console.error('响应内容:', responseText);
      throw new Error('服务器返回的数据格式错误');
    }
    
    console.log('✅ API 响应数据:', data);
    
    // 检查是否登录过期
    if (data.code === -999) {
      handleTokenExpired();
      throw new Error('登录已过期，请重新登录');
    }
    
    return data;
  } catch (error) {
    console.error('API 请求错误:', error);
    
    // 更友好的错误提示
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error('网络连接失败，请检查网络或稍后重试');
    }
    
    throw error;
  }
};

/**
 * 带认证的 API 请求函数（需要登录）
 * @param endpoint - API 端点路径
 * @param options - fetch 请求配置
 * @returns Promise
 */
export const authenticatedRequest = async <T = any>(
  endpoint: string,
  options: {
    method?: string;
    headers?: Record<string, string>;
    body?: any;
    [key: string]: any;
  } = {}
): Promise<T> => {
  // 从 localStorage 获取 token
  const token = localStorage.getItem('authToken');
  
  console.log('🔑 发起认证请求:', endpoint);
  console.log('📝 Token:', token);
  
  if (!token) {
    console.warn('⚠️ 未找到 token，可能需要登录');
    handleTokenExpired();
    throw new Error('未登录，请先登录');
  }
  
  // 在 header 中添加 ApiToken
  const authHeaders: Record<string, string> = {
    ApiToken: token,
  };

  return apiRequest<T>(endpoint, {
    method: 'POST', // 默认使用 POST 请求
    ...options,
    headers: {
      ...authHeaders,
      ...options.headers,
    },
  });
};
